import { useState, useEffect } from "react";
import api from "../utils/axios";
import { useToast } from "@chakra-ui/react";

const useUsers = (locationId) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const toast = useToast();

    useEffect(() => {
        if (!locationId) return;

        const getUsers = async () => {
            try {
                setLoading(true);
                const response = await api.get(`/users/location/${locationId}`);

                const formattedUsers = response.data.map(user => ({
                    ...user,
                    fullName: user.fullName || `${user.name?.trim() || ""} ${user.lastName?.trim() || ""}`.trim(),
                    email: user.email || "No Email Provided",
                }));

                setUsers(formattedUsers);
            } catch (error) {
                toast({
                    title: "Error",
                    description: "Failed to fetch users.",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            } finally {
                setLoading(false);
            }
        };

        getUsers();
    }, [locationId]);

    const handleDeactivate = async (userId) => {
        try {
            await api.put(`/users/deactivate/${locationId}/${userId}`);

            setUsers((prevUsers) => prevUsers.filter(user => user.id !== userId));

            toast({
                title: "User Deactivated",
                description: "The user has been successfully deactivated.",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to deactivate user.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleUpdateUser = async (userId, updatedUser) => {
        try {
            const response = await api.post(`/users/edit/${userId}`, updatedUser);
            const updated = response.data || updatedUser;

            setUsers((prevUsers) =>
                prevUsers.map((user) =>
                    user.id === userId
                        ? {
                              ...user,
                              ...updated,
                              fullName: updated.fullName || `${updated.name?.trim() || ""} ${updated.lastName?.trim() || ""}`.trim(),
                          }
                        : user
                )
            );

            toast({
                title: "User Updated",
                description: "Korisnik je uspješno ažuriran.",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: "Update Failed",
                description: "Došlo je do greške pri ažuriranju korisnika.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            throw error;
        }
    };

    const handleCreateUser = async (newUser) => {
        try {
            const response = await api.post(`/auth/register/${locationId}`, newUser);

            const createdUser = response.data;

            const formattedUser = {
                id: createdUser.id || Math.random().toString(36).substr(2, 9),
                fullName: `${createdUser.name?.trim() || ""} ${createdUser.lastName?.trim() || ""}`.trim(),
                email: createdUser.email || "No Email Provided",
            };

            setUsers((prevUsers) => [formattedUser, ...prevUsers]);

            toast({
                title: "User Created",
                description: `Successfully added ${formattedUser.fullName}.`,
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to create user.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    return { users, loading, handleDeactivate, handleCreateUser, handleUpdateUser };
};

export default useUsers;
